import type { LanguageCode } from '../../shared/types/LanguageType';
import type {
  FolderType,
  RootFolder,
  RootFolderSettings,
  SharedFolder,
  UserData,
  UserResponse,
  WorkspaceRole,
  WorkspaceState,
} from './types';

type FolderWithType = (RootFolder | SharedFolder) & { type: FolderType };

export type ResolvedWorkspace = Pick<WorkspaceState, 'currentFolderId' | 'folderType' | 'role'>;

export type FolderUiSettings = Pick<
  RootFolderSettings,
  'mode' | 'currentLang' | 'isInitialized' | 'accent' | 'themeSchedule'
> & {
  currentLang: LanguageCode;
};

export const mergeUserFromResponse = (
  currentUser: UserData,
  payload: UserResponse
): UserData => {
  const { rootFolders, sharedFolders, ...userData } = payload;

  return {
    ...currentUser,
    ...userData,
    rootFolders: rootFolders || currentUser.rootFolders || [],
    sharedFolders: sharedFolders || currentUser.sharedFolders || [],
  };
};

export const resolveWorkspaceAfterUserUpdate = (
  rootFolders: RootFolder[] | undefined,
  sharedFolders: SharedFolder[] | undefined,
  previousFolderId: string | null,
  previousFolderType: FolderType | null
): ResolvedWorkspace => {
  const allFolders: FolderWithType[] = [
    ...(rootFolders || []).map((folder) => ({ ...folder, type: 'own' as const })),
    ...(sharedFolders || []).map((folder) => ({ ...folder, type: 'shared' as const })),
  ];

  let currentFolderId = previousFolderId;
  let currentFolderType = previousFolderType;
  let role: WorkspaceRole = 'owner';

  if (allFolders.length > 0) {
    if (currentFolderId) {
      const folderExists = allFolders.some((folder) => folder.folderId === currentFolderId);
      if (!folderExists) {
        currentFolderId = null;
        currentFolderType = null;
      } else {
        const folder = allFolders.find((item) => item.folderId === currentFolderId);
        currentFolderType = folder?.type || null;
      }
    }

    if (!currentFolderId) {
      const activeOwnFolder = rootFolders?.find((folder) => folder.isActive);
      if (activeOwnFolder) {
        currentFolderId = activeOwnFolder.folderId;
        currentFolderType = 'own';
      } else if (sharedFolders?.length) {
        currentFolderId = sharedFolders[0].folderId;
        currentFolderType = 'shared';
      } else if (rootFolders?.length) {
        currentFolderId = rootFolders[0].folderId;
        currentFolderType = 'own';
      }
    }
  } else {
    currentFolderId = null;
    currentFolderType = null;
  }

  if (currentFolderId && currentFolderType) {
    const currentOwnFolder = rootFolders?.find((folder) => folder.folderId === currentFolderId);
    const currentSharedFolder = sharedFolders?.find((folder) => folder.folderId === currentFolderId);

    role = currentOwnFolder
      ? 'owner'
      : (currentSharedFolder?.role || 'viewer');
  }

  return {
    currentFolderId,
    folderType: currentFolderType,
    role,
  };
};

export const getFolderUiSettings = (
  rootFolders: RootFolder[] | undefined,
  sharedFolders: SharedFolder[] | undefined,
  currentFolderId: string | null,
  currentFolderType: FolderType | null
): FolderUiSettings | null => {
  if (!currentFolderId || !currentFolderType) {
    return null;
  }

  const currentOwnFolder = rootFolders?.find((folder) => folder.folderId === currentFolderId);
  const currentSharedFolder = sharedFolders?.find((folder) => folder.folderId === currentFolderId);
  const currentFolder = currentOwnFolder || currentSharedFolder;

  if (!currentFolder) {
    return null;
  }

  return {
    mode: currentFolder.settings.mode,
    currentLang: currentFolder.settings.currentLang as LanguageCode,
    isInitialized: currentFolder.settings.isInitialized,
    accent: currentFolder.settings.accent,
    themeSchedule: currentFolder.settings.themeSchedule,
  };
};

export const resolveFolderType = (
  user: UserData,
  folderId: string,
  folderType?: FolderType
): FolderType | null => {
  if (folderType) {
    return folderType;
  }

  if (user.rootFolders?.some((folder) => folder.folderId === folderId)) {
    return 'own';
  }

  if (user.sharedFolders?.some((folder) => folder.folderId === folderId)) {
    return 'shared';
  }

  return null;
};

export const getFolderByType = (
  user: UserData,
  folderId: string,
  folderType: FolderType
): RootFolder | SharedFolder | undefined => {
  if (folderType === 'own') {
    return user.rootFolders?.find((folder) => folder.folderId === folderId);
  }

  return user.sharedFolders?.find((folder) => folder.folderId === folderId);
};

export const getCurrentFolderFromUser = (
  user: UserData,
  currentFolderId: string | null,
  folderType: FolderType | null
): RootFolder | SharedFolder | undefined => {
  if (!currentFolderId || !folderType) {
    return undefined;
  }

  return getFolderByType(user, currentFolderId, folderType);
};
